export type Vaccination = {
  vaccination_id: string;
  vaccine_name: string;
  dose_label: string | null;
  dose_number: number | null;
  series_name: string | null;
  series_total: number | null;
  administered_date: string;
  administered_time: string | null;
  cost_amount: string | null;
  cost_currency: string | null;
  payment_mode: string | null;
  notes: string | null;
  next_dose_due_date: string | null;
  next_dose_due_time: string | null;
  timezone: string | null;
  reminder_enabled: boolean;
  source: string | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AllergyStatus = "active" | "inactive" | "resolved" | "entered_in_error";
export type AllergyCategory = "medication" | "food" | "environmental" | "other";
export type AllergySeverity = "mild" | "moderate" | "severe" | "life_threatening" | "unknown";

export type Allergy = {
  allergy_id: string;
  substance_name: string;
  category: AllergyCategory;
  reaction: string | null;
  severity: AllergySeverity;
  status: AllergyStatus;
  notes: string | null;
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AllergyInput = {
  substance_name: string;
  category: AllergyCategory;
  reaction: string | null;
  severity: AllergySeverity;
  status: AllergyStatus;
  notes: string | null;
};

export type HealthConditionStatus = "active" | "inactive" | "resolved" | "entered_in_error";

export type HealthCondition = {
  condition_id: string;
  condition_name: string;
  status: HealthConditionStatus;
  diagnosed_date: string | null;
  notes: string | null;
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type HealthConditionInput = {
  condition_name: string;
  status: HealthConditionStatus;
  diagnosed_date: string | null;
  notes: string | null;
};

export type AppointmentType = "doctor_visit" | "test_diagnostic" | "therapy_treatment" | "follow_up" | "health_checkup" | "other";
export type AppointmentMode = "in_person" | "video" | "phone";
export type AppointmentStatus = "scheduled" | "cancelled" | "archived";
export type ExtraReminderOption = "2_days_before" | "1_day_before" | "2_hours_before" | "1_hour_before" | "30_minutes_before";

export type Appointment = {
  appointment_id: string;
  appointment_type: AppointmentType;
  title: string;
  scheduled_date: string;
  scheduled_time: string;
  timezone: string;
  provider_name: string | null;
  facility_name: string | null;
  mode: AppointmentMode;
  location: string | null;
  notes: string | null;
  reminder_enabled: boolean;
  extra_reminder: ExtraReminderOption | null;
  status: AppointmentStatus;
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AppointmentInput = {
  appointment_type: AppointmentType;
  title: string;
  scheduled_date: string;
  scheduled_time: string;
  timezone: string;
  provider_name: string | null;
  facility_name: string | null;
  mode: AppointmentMode;
  location: string | null;
  notes: string | null;
  reminder_enabled: boolean;
  extra_reminder: ExtraReminderOption | null;
};

export type VaccinationInput = {
  vaccine_name: string;
  dose_label: string | null;
  dose_number: number | null;
  series_name: string | null;
  series_total: number | null;
  administered_date: string;
  administered_time: string | null;
  cost_amount: string | null;
  cost_currency: string | null;
  payment_mode: string | null;
  notes: string | null;
  next_dose_due_date: string | null;
  next_dose_due_time: string | null;
  timezone: string | null;
  reminder_enabled: boolean;
};

type VaccinationResponse = {
  vaccination?: Partial<Vaccination>;
};

type VaccinationListResponse = {
  vaccinations?: Array<Partial<Vaccination>>;
};

type AllergyResponse = {
  allergy?: Partial<Allergy>;
};

type AllergyListResponse = {
  allergies?: Array<Partial<Allergy>>;
};

type HealthConditionResponse = {
  condition?: Partial<HealthCondition>;
};

type HealthConditionListResponse = {
  conditions?: Array<Partial<HealthCondition>>;
};

type AppointmentResponse = {
  appointment?: Partial<Appointment>;
};

type AppointmentListResponse = {
  appointments?: Array<Partial<Appointment>>;
};

// GET /v1/overview -- the four health-record domains this service owns, from one request. The
// backend applies the same filtering/ordering/normalization as the individual list endpoints.
type HealthOverviewApiResponse = {
  vaccinations?: Array<Partial<Vaccination>>;
  allergies?: Array<Partial<Allergy>>;
  conditions?: Array<Partial<HealthCondition>>;
  appointments?: Array<Partial<Appointment>>;
};

export type HealthOverview = {
  vaccinations: Vaccination[];
  allergies: Allergy[];
  conditions: HealthCondition[];
  appointments: Appointment[];
};

export class HealthRecordServiceError extends Error {
  constructor(message = "We could not reach your vaccination records right now.") {
    super(message);
    this.name = "HealthRecordServiceError";
  }
}

function textOrNull(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function numberOrNull(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeVaccination(record: Partial<Vaccination> | undefined): Vaccination {
  return {
    vaccination_id: textOrNull(record?.vaccination_id) ?? "",
    vaccine_name: textOrNull(record?.vaccine_name) ?? "Vaccination",
    dose_label: textOrNull(record?.dose_label),
    dose_number: numberOrNull(record?.dose_number),
    series_name: textOrNull(record?.series_name),
    series_total: numberOrNull(record?.series_total),
    administered_date: textOrNull(record?.administered_date) ?? "",
    administered_time: textOrNull(record?.administered_time),
    cost_amount: textOrNull(record?.cost_amount),
    cost_currency: textOrNull(record?.cost_currency),
    payment_mode: textOrNull(record?.payment_mode),
    notes: textOrNull(record?.notes),
    next_dose_due_date: textOrNull(record?.next_dose_due_date),
    next_dose_due_time: textOrNull(record?.next_dose_due_time),
    timezone: textOrNull(record?.timezone),
    reminder_enabled: record?.reminder_enabled === true,
    source: textOrNull(record?.source),
    status: textOrNull(record?.status),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

function normalizeAllergy(record: Partial<Allergy> | undefined): Allergy {
  return {
    allergy_id: textOrNull(record?.allergy_id) ?? "",
    substance_name: textOrNull(record?.substance_name) ?? "Allergy",
    category: (textOrNull(record?.category) ?? "other") as AllergyCategory,
    reaction: textOrNull(record?.reaction),
    severity: (textOrNull(record?.severity) ?? "unknown") as AllergySeverity,
    status: (textOrNull(record?.status) ?? "active") as AllergyStatus,
    notes: textOrNull(record?.notes),
    source: textOrNull(record?.source),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

function normalizeCondition(record: Partial<HealthCondition> | undefined): HealthCondition {
  return {
    condition_id: textOrNull(record?.condition_id) ?? "",
    condition_name: textOrNull(record?.condition_name) ?? "Known Health Condition",
    status: (textOrNull(record?.status) ?? "active") as HealthConditionStatus,
    diagnosed_date: textOrNull(record?.diagnosed_date),
    notes: textOrNull(record?.notes),
    source: textOrNull(record?.source),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

function normalizeAppointment(record: Partial<Appointment> | undefined): Appointment {
  return {
    appointment_id: textOrNull(record?.appointment_id) ?? "",
    appointment_type: (textOrNull(record?.appointment_type) ?? "other") as AppointmentType,
    title: textOrNull(record?.title) ?? "Appointment",
    scheduled_date: textOrNull(record?.scheduled_date) ?? "",
    scheduled_time: textOrNull(record?.scheduled_time) ?? "",
    timezone: textOrNull(record?.timezone) ?? "UTC",
    provider_name: textOrNull(record?.provider_name),
    facility_name: textOrNull(record?.facility_name),
    mode: (textOrNull(record?.mode) ?? "in_person") as AppointmentMode,
    location: textOrNull(record?.location),
    notes: textOrNull(record?.notes),
    reminder_enabled: record?.reminder_enabled === true,
    extra_reminder: (textOrNull(record?.extra_reminder) as ExtraReminderOption | null) ?? null,
    status: (textOrNull(record?.status) ?? "scheduled") as AppointmentStatus,
    source: textOrNull(record?.source),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

async function requestHealthRecord<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "POST" | "PATCH",
  body?: TBody,
  signal?: AbortSignal,
) {
  if (!accessToken) {
    throw new HealthRecordServiceError("Please sign in again to manage your vaccination records.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new HealthRecordServiceError("Please check the vaccination details and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new HealthRecordServiceError("Please sign in again to manage your vaccination records.");
    }

    if (response.status === 404) {
      throw new HealthRecordServiceError("We could not find that vaccination record.");
    }

    throw new HealthRecordServiceError();
  }

  return (await response.json()) as unknown;
}

export function createHealthRecordServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async listVaccinations(signal?: AbortSignal) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/vaccinations", "GET", undefined, signal)) as VaccinationListResponse;
      return (data.vaccinations ?? []).map(normalizeVaccination).filter((vaccination) => vaccination.vaccination_id);
    },
    async createVaccination(payload: VaccinationInput) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/vaccinations", "POST", payload)) as VaccinationResponse;
      return normalizeVaccination(data.vaccination);
    },
    async getVaccination(vaccinationId: string, signal?: AbortSignal) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/vaccinations/${encodeURIComponent(vaccinationId)}`,
        "GET",
        undefined,
        signal,
      )) as VaccinationResponse;
      return normalizeVaccination(data.vaccination);
    },
    async updateVaccination(vaccinationId: string, payload: Partial<VaccinationInput>) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/vaccinations/${encodeURIComponent(vaccinationId)}`,
        "PATCH",
        payload,
      )) as VaccinationResponse;
      return normalizeVaccination(data.vaccination);
    },
    async listHealthOverview(signal?: AbortSignal): Promise<HealthOverview> {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/overview", "GET", undefined, signal)) as HealthOverviewApiResponse;
      return {
        vaccinations: (data.vaccinations ?? []).map(normalizeVaccination).filter((vaccination) => vaccination.vaccination_id),
        allergies: (data.allergies ?? []).map(normalizeAllergy).filter((allergy) => allergy.allergy_id),
        conditions: (data.conditions ?? []).map(normalizeCondition).filter((condition) => condition.condition_id),
        appointments: (data.appointments ?? []).map(normalizeAppointment).filter((appointment) => appointment.appointment_id),
      };
    },
    async listAllergies(signal?: AbortSignal) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/allergies", "GET", undefined, signal)) as AllergyListResponse;
      return (data.allergies ?? []).map(normalizeAllergy).filter((allergy) => allergy.allergy_id);
    },
    async createAllergy(payload: AllergyInput) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/allergies", "POST", payload)) as AllergyResponse;
      return normalizeAllergy(data.allergy);
    },
    async updateAllergy(allergyId: string, payload: Partial<AllergyInput>) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/allergies/${encodeURIComponent(allergyId)}`,
        "PATCH",
        payload,
      )) as AllergyResponse;
      return normalizeAllergy(data.allergy);
    },
    async listConditions(signal?: AbortSignal) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/conditions", "GET", undefined, signal)) as HealthConditionListResponse;
      return (data.conditions ?? []).map(normalizeCondition).filter((condition) => condition.condition_id);
    },
    async createCondition(payload: HealthConditionInput) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/conditions", "POST", payload)) as HealthConditionResponse;
      return normalizeCondition(data.condition);
    },
    async updateCondition(conditionId: string, payload: Partial<HealthConditionInput>) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/conditions/${encodeURIComponent(conditionId)}`,
        "PATCH",
        payload,
      )) as HealthConditionResponse;
      return normalizeCondition(data.condition);
    },
    async listAppointments(signal?: AbortSignal) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/appointments", "GET", undefined, signal)) as AppointmentListResponse;
      return (data.appointments ?? []).map(normalizeAppointment).filter((appointment) => appointment.appointment_id);
    },
    async createAppointment(payload: AppointmentInput) {
      const data = (await requestHealthRecord(apiBaseUrl, getAccessToken(), "/v1/appointments", "POST", payload)) as AppointmentResponse;
      return normalizeAppointment(data.appointment);
    },
    async getAppointment(appointmentId: string, signal?: AbortSignal) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/appointments/${encodeURIComponent(appointmentId)}`,
        "GET",
        undefined,
        signal,
      )) as AppointmentResponse;
      return normalizeAppointment(data.appointment);
    },
    async updateAppointment(appointmentId: string, payload: Partial<AppointmentInput> | { status: AppointmentStatus }) {
      const data = (await requestHealthRecord(
        apiBaseUrl,
        getAccessToken(),
        `/v1/appointments/${encodeURIComponent(appointmentId)}`,
        "PATCH",
        payload,
      )) as AppointmentResponse;
      return normalizeAppointment(data.appointment);
    },
  };
}
